import React, { useEffect, useMemo, useRef, useState } from 'react';
import firestoreService from '../../shared/services/firestoreService';
import { DEFAULT_SEGMENT_RULES, SEGMENT_META } from '../../shared/customerSegmentation';
import type { SegmentRules } from '../../shared/types';
import { SpinnerIcon } from './icons/SpinnerIcon';

interface Props {
    restaurantId: number | string;
    initialRules: SegmentRules;
    onClose: () => void;
    /**
     * Appelé après un enregistrement réussi.
     * `recomputed` : true si le recalcul a aussi réussi ; `warning` : message si le recalcul a échoué.
     */
    onSaved: (recomputed: boolean, warning?: string) => void;
}

const errorCode = (e: unknown): string | undefined =>
    typeof e === 'object' && e !== null && 'code' in e ? String((e as { code: unknown }).code) : undefined;

/**
 * Champ numérique qui garde la saisie en texte pendant l'édition
 * (sinon effacer « 30 » force immédiatement la valeur minimale).
 */
const NumberField: React.FC<{
    id: string;
    label: string;
    value: number;
    min?: number;
    integer?: boolean;
    suffix?: string;
    disabled?: boolean;
    onChange: (v: number) => void;
}> = ({ id, label, value, min = 0, integer = true, suffix, disabled, onChange }) => {
    const [text, setText] = useState(String(value));
    useEffect(() => { setText(String(value)); }, [value]);

    const commit = () => {
        const n = Number(text.replace(',', '.'));
        if (!Number.isFinite(n)) { setText(String(value)); return; }
        const clamped = Math.max(min, integer ? Math.round(n) : Math.round(n * 100) / 100);
        setText(String(clamped));
        if (clamped !== value) onChange(clamped);
    };

    return (
        <div className={disabled ? 'opacity-50' : ''}>
            <label htmlFor={id} className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
            <div className="flex items-center">
                <input
                    id={id}
                    type="number"
                    inputMode={integer ? 'numeric' : 'decimal'}
                    min={min}
                    step={integer ? 1 : 0.01}
                    disabled={disabled}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
                    className="w-full border border-gray-400 rounded-lg p-2 text-sm focus:ring-brand-primary focus:border-brand-primary disabled:bg-gray-100"
                />
                {suffix && <span className="ml-2 text-sm text-gray-500 whitespace-nowrap">{suffix}</span>}
            </div>
        </div>
    );
};

const Section: React.FC<{
    title: string;
    color: string;
    enabled: boolean;
    onToggle: (v: boolean) => void;
    children: React.ReactNode;
}> = ({ title, color, enabled, onToggle, children }) => (
    <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className={`px-2 font-bold ${color}`}>{title}</legend>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer mb-3">
            <input type="checkbox" checked={enabled} onChange={e => onToggle(e.target.checked)} className="rounded" aria-label={`Activer le segment ${title}`} />
            Segment actif
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </fieldset>
);

const SegmentRulesEditor: React.FC<Props> = ({ restaurantId, initialRules, onClose, onSaved }) => {
    const [rules, setRules] = useState<SegmentRules>(initialRules);
    const [busy, setBusy] = useState<'save' | 'recompute' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    const dirty = useMemo(() => JSON.stringify(rules) !== JSON.stringify(initialRules), [rules, initialRules]);

    // Focus initial, Échap pour fermer, retour du focus à l'ouvreur.
    useEffect(() => {
        const opener = document.activeElement as HTMLElement | null;
        dialogRef.current?.querySelector<HTMLElement>('input, button')?.focus();
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && busy === null) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            opener?.focus?.();
        };
    }, [onClose, busy]);

    const validate = (): string | null => {
        if (!Number.isInteger(rules.windowDays) || rules.windowDays < 1) return 'La fenêtre doit être un nombre entier de jours, au moins 1.';
        if (rules.nouveau.minVisits > rules.nouveau.maxVisits) return 'Nouveau : le minimum de visites dépasse le maximum.';
        return null;
    };

    const save = async (recompute: boolean) => {
        const err = validate();
        if (err) { setError(err); return; }
        setError(null);
        setBusy(recompute ? 'recompute' : 'save');
        try {
            await firestoreService.saveSegmentRules(restaurantId, rules);
        } catch (e) {
            setError('Enregistrement impossible. Vérifiez votre connexion et réessayez.');
            console.error('saveSegmentRules', e);
            setBusy(null);
            return;
        }
        if (!recompute) { setBusy(null); onSaved(false); return; }
        try {
            await firestoreService.recomputeSegments(restaurantId);
            setBusy(null);
            onSaved(true);
        } catch (e) {
            console.error('recomputeSegments', e);
            setBusy(null);
            onSaved(false, errorCode(e) === 'functions/resource-exhausted'
                ? 'Règles enregistrées, mais trop de recalculs cette heure-ci : le calcul de nuit les appliquera.'
                : 'Règles enregistrées, mais le recalcul a échoué : le calcul de nuit les appliquera.');
        }
    };

    const M = SEGMENT_META;

    return (
        <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={() => { if (!dirty && busy === null) onClose(); }}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="segment-rules-title"
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                <div className="p-6 border-b flex items-start justify-between">
                    <div>
                        <h3 id="segment-rules-title" className="text-xl font-bold text-gray-900">Règles de segmentation</h3>
                        <p className="text-sm text-gray-500 mt-1">
                            Les segments sont recalculés chaque nuit. Si un client remplit plusieurs règles, la priorité est :
                            À risque, puis VIP, puis Gros panier, puis Habitué, puis Nouveau.
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none" aria-label="Fermer">×</button>
                </div>

                <div className="p-6 space-y-5">
                    <NumberField
                        id="seg-window"
                        label="Fenêtre d'observation"
                        value={rules.windowDays}
                        min={1}
                        suffix="jours"
                        onChange={v => setRules({ ...rules, windowDays: v })}
                    />

                    <Section title={M.nouveau.label} color={M.nouveau.color} enabled={rules.nouveau.enabled} onToggle={v => setRules({ ...rules, nouveau: { ...rules.nouveau, enabled: v } })}>
                        <NumberField id="seg-nouveau-min" label="Visites minimum" value={rules.nouveau.minVisits} min={1} disabled={!rules.nouveau.enabled} onChange={v => setRules({ ...rules, nouveau: { ...rules.nouveau, minVisits: v } })} />
                        <NumberField id="seg-nouveau-max" label="Visites maximum" value={rules.nouveau.maxVisits} min={1} disabled={!rules.nouveau.enabled} onChange={v => setRules({ ...rules, nouveau: { ...rules.nouveau, maxVisits: v } })} />
                        <p className="sm:col-span-2 text-xs text-gray-500">Première visite dans la fenêtre, et nombre de visites compris entre ces deux bornes.</p>
                    </Section>

                    <Section title={M.habitue.label} color={M.habitue.color} enabled={rules.habitue.enabled} onToggle={v => setRules({ ...rules, habitue: { ...rules.habitue, enabled: v } })}>
                        <NumberField id="seg-habitue-min" label="Visites minimum sur la fenêtre" value={rules.habitue.minVisits} min={1} disabled={!rules.habitue.enabled} onChange={v => setRules({ ...rules, habitue: { ...rules.habitue, minVisits: v } })} />
                    </Section>

                    <Section title={M.vip.label} color={M.vip.color} enabled={rules.vip.enabled} onToggle={v => setRules({ ...rules, vip: { ...rules.vip, enabled: v } })}>
                        <NumberField id="seg-vip-spend" label="Dépenses minimum" value={rules.vip.minSpend} integer={false} suffix="€" disabled={!rules.vip.enabled} onChange={v => setRules({ ...rules, vip: { ...rules.vip, minSpend: v } })} />
                        <NumberField id="seg-vip-visits" label="ou visites minimum" value={rules.vip.minVisits} min={1} disabled={!rules.vip.enabled} onChange={v => setRules({ ...rules, vip: { ...rules.vip, minVisits: v } })} />
                        <p className="sm:col-span-2 text-xs text-gray-500">Un client est VIP s'il atteint l'un des deux seuils sur la fenêtre.</p>
                    </Section>

                    <Section title={M.gros_panier.label} color={M.gros_panier.color} enabled={rules.grosPanier.enabled} onToggle={v => setRules({ ...rules, grosPanier: { ...rules.grosPanier, enabled: v } })}>
                        <NumberField id="seg-gp-ticket" label="Panier moyen minimum" value={rules.grosPanier.minAverageTicket} integer={false} suffix="€" disabled={!rules.grosPanier.enabled} onChange={v => setRules({ ...rules, grosPanier: { ...rules.grosPanier, minAverageTicket: v } })} />
                        <NumberField id="seg-gp-visits" label="Visites minimum sur la fenêtre" value={rules.grosPanier.minVisits} min={1} disabled={!rules.grosPanier.enabled} onChange={v => setRules({ ...rules, grosPanier: { ...rules.grosPanier, minVisits: v } })} />
                    </Section>

                    <Section title={M.a_risque.label} color={M.a_risque.color} enabled={rules.aRisque.enabled} onToggle={v => setRules({ ...rules, aRisque: { ...rules.aRisque, enabled: v } })}>
                        <NumberField id="seg-risque-days" label="Jours sans visite" value={rules.aRisque.inactiveDays} min={1} suffix="jours" disabled={!rules.aRisque.enabled} onChange={v => setRules({ ...rules, aRisque: { ...rules.aRisque, inactiveDays: v } })} />
                        <p className="sm:col-span-2 text-xs text-gray-500">Ne concerne que les clients déjà venus au moins une fois.</p>
                    </Section>

                    {error && <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">{error}</p>}
                </div>

                <div className="p-6 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => setRules(DEFAULT_SEGMENT_RULES)}
                        className="text-sm text-gray-500 hover:text-gray-800 underline text-left"
                    >
                        Revenir aux valeurs par défaut
                    </button>
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} disabled={busy !== null} className="px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                            Annuler
                        </button>
                        <button type="button" disabled={busy !== null} onClick={() => save(false)} className="px-4 py-2 rounded-lg border border-brand-primary text-sm font-medium text-brand-primary hover:bg-brand-light disabled:opacity-50 flex items-center gap-2">
                            {busy === 'save' && <SpinnerIcon className="w-4 h-4 animate-spin" />}
                            Enregistrer
                        </button>
                        <button type="button" disabled={busy !== null} onClick={() => save(true)} className="px-4 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                            {busy === 'recompute' && <SpinnerIcon className="w-4 h-4 animate-spin" />}
                            Enregistrer et recalculer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SegmentRulesEditor;
